import React, { FC, useState } from 'react';
import { Image, Spin, Upload, notification } from 'antd';
import { RcFile } from 'antd/lib/upload';
import { CameraIcon, PlusIcon, TrashIcon } from 'components/icons';
import { useImageBlobSrc } from 'hooks';
import { MIMEType } from 'types/MIME-types';
import { pdfToImage } from 'utils/pdf-to-image';
import styles from './styles.less';

type CertificateImageProps = {
  src: string;
  onChangeFile: (file: Blob | null) => void;
};

const getBase64 = (file: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

const allowedCertificateTypes = [
  MIMEType.APPLICATION_PDF,
  MIMEType.IMAGE_JPEG,
  MIMEType.IMAGE_JPG,
  MIMEType.IMAGE_PNG,
] as string[];

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const MAX_SIZE_ALLOWED_BYTES = 2 * 1024 * 1024; // 2MB

export const CertificateImage: FC<CertificateImageProps> = ({
  src,
  onChangeFile,
}) => {
  const { src: imageSrc, isImageLoading } = useImageBlobSrc(src);

  const [uploadedImageBase64, setUploadedImage] = useState<
    string | undefined | null
  >(undefined);

  const handleUpload = async (file: RcFile): Promise<boolean> => {
    if (!allowedCertificateTypes.includes(file.type)) {
      notification.error({
        message: 'Incorrect extension. Available: .pdf, .jpeg, .jpg, .png',
      });

      return false;
    }

    if (file.size > MAX_SIZE_ALLOWED_BYTES) {
      notification.error({
        message: 'Incorrect file size. 2MB available',
      });

      return false;
    }

    if (file.type === MIMEType.APPLICATION_PDF) {
      let imageFile;

      try {
        imageFile = await pdfToImage(file);
      } catch (e) {
        notification.error({
          message: 'Something has gone wrong. Try again!',
        });
        console.error(e);
        return false;
      }

      setUploadedImage(await getBase64(imageFile));
      onChangeFile(imageFile);

      return false;
    }

    setUploadedImage(await getBase64(file));
    onChangeFile(file);

    return false;
  };

  const onDelete = (): void => {
    setUploadedImage(null);
    onChangeFile(null);
  };

  const previewSrc =
    uploadedImageBase64 === null ? undefined : uploadedImageBase64 || imageSrc;

  const imageContainerClass = styles['image-container'];

  if (isImageLoading) {
    return (
      <div className={imageContainerClass}>
        <Spin />
      </div>
    );
  }

  if (previewSrc) {
    return (
      <div className={imageContainerClass}>
        <Image
          src={previewSrc}
          preview={false}
          className={styles['certificate-image']}
        />
        <div className={styles['tools']}>
          <Upload
            multiple={false}
            showUploadList={false}
            maxCount={1}
            beforeUpload={handleUpload}
          >
            <CameraIcon />
          </Upload>
          <TrashIcon onClick={onDelete} />
        </div>
      </div>
    );
  }

  return (
    <div className={imageContainerClass}>
      <Upload
        multiple={false}
        showUploadList={false}
        maxCount={1}
        beforeUpload={handleUpload}
      >
        <div className={styles['empty-image']}>
          <PlusIcon />
          Upload
        </div>
      </Upload>
    </div>
  );
};
